import React, { useEffect, useRef, useState } from 'react';
import { Button } from './UI';
import { listMessages, sendMessage } from '../api';
import { Loader2, Send, X, MessageSquare, DollarSign } from 'lucide-react';

export default function NegotiationChat({ shipmentId, carrier, onClose }) {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [text, setText] = useState('');
  const [rateOffer, setRateOffer] = useState('');
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);

  const load = async () => {
    try {
      const m = await listMessages(shipmentId, carrier.id);
      setMessages(m);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shipmentId, carrier.id]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const handleSend = async () => {
    if (!text.trim() && !rateOffer) return;
    setSending(true);
    try {
      const newMsgs = await sendMessage(
        shipmentId,
        carrier.id,
        text.trim() || `Can you do ${rateOffer ? `$${rateOffer}` : 'better'}?`,
        rateOffer ? Number(rateOffer) : null,
      );
      setMessages((prev) => [...prev, ...newMsgs]);
      setText('');
      setRateOffer('');
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      className="fixed inset-0 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4"
      style={{ background: 'rgba(15,23,42,.45)' }}
      onClick={onClose}
    >
      <div
        className="bg-white w-full sm:max-w-lg rounded-t-xl sm:rounded-xl flex flex-col"
        style={{
          height: '85vh',
          maxHeight: 640,
          boxShadow: '0 20px 50px rgba(15,23,42,.2)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          className="flex items-center justify-between px-4 py-3"
          style={{ borderBottom: '1px solid var(--lf-line)' }}
        >
          <div className="flex items-center gap-2">
            <MessageSquare size={16} style={{ color: 'var(--lf-blue)' }} />
            <div>
              <div className="font-semibold text-sm">Negotiate with {carrier.name}</div>
              <div className="text-xs" style={{ color: 'var(--lf-muted)' }}>
                Posted rate {carrier.rate} • on-time {carrier.on_time}
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            style={{ background: 'transparent', border: 'none', cursor: 'pointer' }}
          >
            <X size={18} />
          </button>
        </div>

        <div
          ref={scrollRef}
          className="flex-1 overflow-y-auto px-4 py-3 space-y-2"
          style={{ background: '#f8faff' }}
        >
          {loading ? (
            <div
              className="flex items-center gap-2 text-sm"
              style={{ color: 'var(--lf-muted)' }}
            >
              <Loader2 className="animate-spin" size={14} /> Loading conversation…
            </div>
          ) : messages.length === 0 ? (
            <div className="text-center text-sm py-10" style={{ color: 'var(--lf-muted)' }}>
              No messages yet. Start the negotiation below.
            </div>
          ) : (
            messages.map((m) => {
              const mine = m.sender === 'shipper';
              return (
                <div
                  key={m.id}
                  className={`flex ${mine ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className="max-w-[80%] px-3 py-2 rounded-xl text-sm"
                    style={{
                      background: mine ? 'var(--lf-blue)' : '#ffffff',
                      color: mine ? '#ffffff' : '#111827',
                      border: mine ? 'none' : '1px solid var(--lf-line)',
                    }}
                  >
                    <div>{m.text}</div>
                    {m.rate_offer ? (
                      <div
                        className="text-xs mt-1 font-semibold inline-flex items-center gap-1"
                        style={{
                          color: mine ? 'rgba(255,255,255,.9)' : 'var(--lf-green)',
                        }}
                      >
                        <DollarSign size={11} /> ${Number(m.rate_offer).toLocaleString()}
                      </div>
                    ) : null}
                  </div>
                </div>
              );
            })
          )}
        </div>

        <div className="px-4 py-3" style={{ borderTop: '1px solid var(--lf-line)' }}>
          <div className="flex gap-2 mb-2">
            <div className="relative" style={{ width: 110 }}>
              <DollarSign
                size={14}
                style={{
                  position: 'absolute',
                  left: 10,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: 'var(--lf-muted)',
                }}
              />
              <input
                type="number"
                value={rateOffer}
                onChange={(e) => setRateOffer(e.target.value)}
                placeholder="Offer"
                className="w-full pl-7 pr-2 py-2 rounded-lg text-sm outline-none"
                style={{ border: '1px solid #d8dde6' }}
              />
            </div>
            <input
              value={text}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSend();
                }
              }}
              placeholder="Write a message…"
              className="flex-1 px-3 py-2 rounded-lg text-sm outline-none"
              style={{ border: '1px solid #d8dde6' }}
            />
            <Button variant="primary" onClick={handleSend} disabled={sending}>
              {sending ? <Loader2 className="animate-spin" size={14} /> : <Send size={14} />}
              Send
            </Button>
          </div>
          <div className="text-xs" style={{ color: 'var(--lf-muted)' }}>
            Tip: enter a dollar amount to send a formal counter-offer.
          </div>
        </div>
      </div>
    </div>
  );
}
